/**
 * ReleaseStateMapPane — Read-only release state map
 *
 * Visually summarizes archived governance ratchets, lineage relationships,
 * and current system release state.
 *
 * SAFETY: Read-only inspection only. No execution authority, no deployment controls.
 */

import React from 'react';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface ReleaseStateMapPaneProps {
  model?: {
    // Optional model for future extensibility
  };
}

interface ReleaseStateEntry {
  ratchetId: string;
  ratchetName: string;
  archivedStatus: 'completed' | 'archived';
  archiveHash: string;
  continuityRole: string;
  immutable: boolean;
  executionAuthority: boolean;
  lineageRelationship?: string;
}

const RELEASE_STATE_ENTRIES: ReleaseStateEntry[] = [
  {
    ratchetId: 'governed-improvement-public-verifier-release-archive',
    ratchetName: 'Public Verifier Release',
    archivedStatus: 'completed',
    archiveHash: '30d7aa5b697aeb053f81a1a156accc02dbb207a002432f1ee4268aaddb7ccf62',
    continuityRole: 'Verifier lineage foundation',
    immutable: true,
    executionAuthority: false,
    lineageRelationship: 'Foundation',
  },
  {
    ratchetId: 'governed-workspace-improvement-pane-release-archive',
    ratchetName: 'Workspace Improvement Pane',
    archivedStatus: 'completed',
    archiveHash: 'b4c1fbdef72ac1c42be3eb589c493fa70dfcf51d59160d4d32dd3e8bc08324fe',
    continuityRole: 'Governed UI persistence',
    immutable: true,
    executionAuthority: false,
    lineageRelationship: 'Builds on Public Verifier',
  },
  {
    ratchetId: 'governed-workspace-trace-explorer-continuity-archive',
    ratchetName: 'Trace Explorer Continuity',
    archivedStatus: 'completed',
    archiveHash: 'e9de4929153910a819b459a6191cc356ed5e82016461467720eb5bfdbfa996fe',
    continuityRole: 'Replay/continuity inspection',
    immutable: true,
    executionAuthority: false,
    lineageRelationship: 'Builds on Improvement Pane',
  },
  {
    ratchetId: 'governed-workspace-audit-surface-archive',
    ratchetName: 'Audit Summary Surface',
    archivedStatus: 'completed',
    archiveHash: '88e12aaf4665242895154f5376f17bbc613da823b838fb5592ea3e96bf533951',
    continuityRole: 'Audit surface lineage',
    immutable: true,
    executionAuthority: false,
    lineageRelationship: 'Builds on Trace Explorer',
  },
  {
    ratchetId: 'governed-workspace-continuity-timeline-archive',
    ratchetName: 'Continuity Timeline Summary',
    archivedStatus: 'completed',
    archiveHash: 'aeed381ed747b3cabbf0601370d85d9fd937a1b3f15a1bd92444382b83485d14',
    continuityRole: 'Continuity timeline visualization',
    immutable: true,
    executionAuthority: false,
    lineageRelationship: 'Builds on Audit Surface',
  },
];

export const ReleaseStateMapPane: React.FC<ReleaseStateMapPaneProps> = ({ model }) => {
  const truncateHash = (hash: string) => `${hash.slice(0, 16)}…`;

  const renderReleaseStateEntry = (entry: ReleaseStateEntry, index: number) => (
    <div key={entry.ratchetId} style={{
      padding: SPACING.sp2,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 2,
      marginBottom: SPACING.sp2,
      position: 'relative',
    }}>
      {/* Lineage connector line */}
      {index < RELEASE_STATE_ENTRIES.length - 1 && (
        <div style={{
          position: 'absolute',
          left: '24px',
          top: '48px',
          bottom: '-16px',
          width: 2,
          backgroundColor: NEUTRAL.borderSubtle,
          zIndex: 0,
        }} />
      )}

      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACING.sp2,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Lineage node */}
        <div style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: SEMANTIC.stabilized.text,
          border: `2px solid ${NEUTRAL.bg200}`,
          flexShrink: 0,
          marginTop: 2,
        }} />

        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: SPACING.sp1,
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.caption.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {entry.ratchetName}
            </span>
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '1px 4px',
              borderRadius: 2,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}>
              {entry.archivedStatus}
            </span>
          </div>

          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            fontFamily: TYPOGRAPHY.mono.family,
            marginBottom: SPACING.sp1,
          }}>
            {truncateHash(entry.archiveHash)}
          </div>

          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            {entry.continuityRole}
          </div>

          {entry.lineageRelationship && (
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: SEMANTIC.stabilized.text,
              marginBottom: SPACING.sp1,
            }}>
              {entry.lineageRelationship}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: SPACING.sp2,
            fontSize: TYPOGRAPHY.micro.size,
          }}>
            <span style={{ color: entry.immutable ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
              immutable: {String(entry.immutable)}
            </span>
            <span style={{ color: !entry.executionAuthority ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
              executionAuthority: {String(entry.executionAuthority)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      padding: SPACING.sp3,
      height: '100%',
      overflowY: 'auto',
      backgroundColor: NEUTRAL.bg200,
    }}>
      {/* Header */}
      <div style={{
        marginBottom: SPACING.sp3,
        paddingBottom: SPACING.sp2,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp1,
        }}>
          RELEASE STATE MAP
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.caption.size,
          color: NEUTRAL.text400,
        }}>
          Visual summary of archived governance ratchets and lineage relationships
        </div>
      </div>

      {/* Release Summary */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Release Summary
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <span>• <strong>Canonical Ratchets:</strong> 5</span>
          <span>• <strong>Archived Ratchets:</strong> 5</span>
          <span>• <strong>Active Ratchets:</strong> 0</span>
          <span>• <strong>Archive Status:</strong> Immutable / Read-only</span>
          <span>• <strong>Replayable Lineage:</strong> Available</span>
          <span>• <strong>Execution Authority:</strong> false</span>
          <span>• <strong>Verification Posture:</strong> Proof-only</span>
        </div>
      </div>

      {/* Archive Index */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Archive Index
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          {RELEASE_STATE_ENTRIES.map((entry) => (
            <div key={entry.ratchetId} style={{
              padding: SPACING.sp1,
              backgroundColor: NEUTRAL.bg700,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
            }}>
              <div style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontWeight: 600,
                color: NEUTRAL.text500,
                marginBottom: SPACING.sp1,
              }}>
                {entry.ratchetName}
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontFamily: TYPOGRAPHY.mono.family,
                marginBottom: SPACING.sp1,
              }}>
                {truncateHash(entry.ratchetId)}
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: SPACING.sp1,
                fontSize: TYPOGRAPHY.micro.size,
              }}>
                <span>• <strong>Status:</strong> {entry.archivedStatus}</span>
                <span>• <strong>Immutable:</strong> {String(entry.immutable)}</span>
                <span>• <strong>Replayable Lineage:</strong> true</span>
                <span>• <strong>Execution Authority:</strong> {String(entry.executionAuthority)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Archive Audit */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Archive Audit
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <span>• <strong>Canonical Ratchets:</strong> 5</span>
          <span>• <strong>Archived Ratchets:</strong> 5</span>
          <span>• <strong>Active Ratchets:</strong> 0</span>
          <span>• <strong>Immutable Archive:</strong> true</span>
          <span>• <strong>Replayable Lineage:</strong> true</span>
          <span>• <strong>Execution Authority:</strong> false</span>
          <span>• <strong>Proof Only:</strong> true</span>
          <span>• <strong>Raw Private Content Exposed:</strong> false</span>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          AUDIT VERDICT: PASSED
        </div>
      </div>

      {/* Verified Proposal Memory Surface */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Proposal Memory Surface
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Surface:</strong> proposal-memory</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Total Checks:</strong> 13</span>
          </div>
          <div>
            <span>• <strong>Passed:</strong> 13</span>
          </div>
          <div>
            <span>• <strong>Failed:</strong> 0</span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Human Review Required:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED SURFACE: READ-ONLY
        </div>
      </div>

      {/* Verified Surface Index */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Surface Index
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Surface:</strong> verified-surface-index</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Total Surfaces:</strong> 4</span>
          </div>
          <div>
            <span>• <strong>Total Checks:</strong> 39</span>
          </div>
          <div>
            <span>• <strong>Passed:</strong> 39</span>
          </div>
          <div>
            <span>• <strong>Failed:</strong> 0</span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Raw Private Content Exposed:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED SURFACE: READ-ONLY
        </div>
      </div>

      {/* Verified Surface Federation */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Surface Federation
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Surface:</strong> surface-federation</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Federated Surfaces:</strong> 4</span>
          </div>
          <div>
            <span>• <strong>Total Verified Checks:</strong> 39</span>
          </div>
          <div>
            <span>• <strong>Failed Checks:</strong> 0</span>
          </div>
          <div>
            <span>• <strong>Federation Mode:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>read-only</span></span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Authority Merged:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Raw Private Content Exposed:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED FEDERATION: READ-ONLY
        </div>
      </div>

      {/* Verified Federated Surface Index */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Federated Surface Index
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Surface:</strong> federated-surface-index</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Total References:</strong> 8</span>
          </div>
          <div>
            <span>• <strong>Surface Reference Count:</strong> 5</span>
          </div>
          <div>
            <span>• <strong>Release-Map Link Reference Count:</strong> 3</span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Authority Merged:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Raw Private Content Exposed:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED FEDERATION INDEX: READ-ONLY
        </div>
      </div>

      {/* Verified Boundary Composition */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Boundary Composition
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Surface:</strong> boundary-composition</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Verified Surface Count:</strong> 5</span>
          </div>
          <div>
            <span>• <strong>Release Map Link Count:</strong> 4</span>
          </div>
          <div>
            <span>• <strong>Federated Reference Count:</strong> 8</span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Authority Merged:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Authority Inherited:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Raw Private Content Exposed:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED BOUNDARY COMPOSITION: READ-ONLY
        </div>
      </div>

      {/* Verified Composition Stabilization */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Verified Composition Stabilization
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div>
            <span>• <strong>Scope:</strong> workspace-composition-stabilization</span>
          </div>
          <div>
            <span>• <strong>Stabilization:</strong> STABILIZED</span>
          </div>
          <div>
            <span>• <strong>Total Checks:</strong> 17</span>
          </div>
          <div>
            <span>• <strong>Passed Checks:</strong> 17</span>
          </div>
          <div>
            <span>• <strong>Failed Checks:</strong> 0</span>
          </div>
          <div>
            <span>• <strong>Verified Surface Count:</strong> 5</span>
          </div>
          <div>
            <span>• <strong>Release Map Link Count:</strong> 4</span>
          </div>
          <div>
            <span>• <strong>Federated Reference Count:</strong> 8</span>
          </div>
          <div>
            <span>• <strong>Receipt Verification:</strong> <span style={{ color: SEMANTIC.stabilized.text, fontWeight: 600 }}>PASSED</span></span>
          </div>
          <div>
            <span>• <strong>Authority Merged:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Authority Inherited:</strong> <span style={{ color: SEMANTIC.refusal.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Execution Authority:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
          <div>
            <span>• <strong>Proof Only:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Replayable:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div>
            <span>• <strong>Immutable Reference:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>true</span></span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span>• <strong>Raw Private Content Exposed:</strong> <span style={{ color: SEMANTIC.stabilized.text }}>false</span></span>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: SEMANTIC.stabilized.chip,
          border: `1px solid ${SEMANTIC.stabilized.text}`,
          borderRadius: 2,
          textAlign: 'center',
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
        }}>
          VERIFIED COMPOSITION STABILIZATION: READ-ONLY
        </div>
      </div>

      {/* Summary counters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: SPACING.sp2,
        marginBottom: SPACING.sp3,
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
      }}>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Canonical Ratchets
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
          }}>
            5
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Archived Ratchets
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
          }}>
            5
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Active Ratchets
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
          }}>
            0
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Replayable Lineage
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
          }}>
            true
          </div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Execution Authority
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
          }}>
            false
          </div>
        </div>
      </div>

      {/* Release state entries */}
      <div style={{ marginBottom: SPACING.sp3 }}>
        {RELEASE_STATE_ENTRIES.map((entry, index) => renderReleaseStateEntry(entry, index))}
      </div>

      {/* Lineage visualization note */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp2,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Lineage Progression
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          Ordered archive progression showing relationships between Improvement Pane, Trace Explorer, Audit Surface, and Continuity Timeline
        </div>
      </div>

      {/* Safety labels */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Safety Labels
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <span>• Read-only release state map</span>
          <span>• Immutable archived lineage</span>
          <span>• Replayable lineage</span>
          <span>• No execution authority</span>
        </div>
      </div>

      {/* Read-only notice */}
      <div style={{
        marginTop: SPACING.sp3,
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
      }}>
        <strong>Read-only inspection only.</strong> This release state map displays archived ratchet lineage only. It does not deploy, rollback, mutate state, or grant execution authority.
      </div>
    </div>
  );
};
